import React, { useState } from 'react';
import Wrapper from './styles';
import { translate } from '../../../../services/translate';
import { useRouter } from '../../../../services/router';
import { useStore } from '../../../../hooks/useStore';
import { useContestPreSelectionState, useFetchContest } from '../../../../hooks/contests';
import UiButton from '../../../ui/button/UiButton';
import UiDotsLoader from '../../../ui/dots-loader/UiDotsLoader';
import UiAlert from '../../../ui/alert/UiAlert';
import { sendContestPrediction } from '../../../../microservices/contests';
import SportContestsBetslipHeader from './header/SportContestsBetslipHeader';
import SportContestsBetslipCounter from './counter/SportContestsBetslipCounter';
import SportContestsBetslipSelection from './selection/SportContestsBetslipSelection';
import { useGlobalModal } from '../../../../hooks/useGlobalModal';
import SportContestsBetslipResubmitModal from './resubmit-modal/SportContestsBetslipResubmitModal';
import { Prediction, PredictionData, PredictionPlatform, PredictionStatus } from '@staycool/contests-types/prediction';
import { stores } from '../../../../stores';
import { contestSelections } from '../../../../stores/contest-selections';
import { logger } from '../../../../services/logger';

export default function SportContestsBetslip() {
    const [isDisabled, setIsDisabled] = useState(false);
    const { contestId } = useRouter().params;
    const [user] = useStore(stores.user);
    const [entries] = useStore(contestSelections.entries);
    const {
        activeEntry,
        getSelections,
        selections,
        clearSelections,
        removeSelection,
        isResubmit,
        setInitialSelections,
        getPredictionsForSend,
        setEntries,
    } = useContestPreSelectionState();
    const { isLoading, contest } = useFetchContest(contestId ?? activeEntry?.contest_id);
    const { showModal, hideModal } = useGlobalModal();

    if ((!contestId && !activeEntry?.contest_id) || !contest || !activeEntry) {
        return null;
    }

    const entry = entries.find((e) => e.id === activeEntry.id);

    if (
        entry &&
        entry?.predictions?.filter((pre) => pre.leg_id == contest.meta?.active_leg?.id).length > 0 &&
        !isResubmit
    ) {
        return null;
    }

    if (entry && entry.status !== 'ONGOING') {
        return null;
    }

    const selectedOutcomesCount = getSelections().length;
    const requiredSelections = contest.meta?.active_leg?.required_selections || 0;
    const activeLeg = contest.legs!.find((leg) => leg.id === contest.meta?.active_leg?.id);

    function updateEntriesAfterSubmit(submittedPredictions: PredictionData[]) {
        const updatedEntries = [...entries];

        updatedEntries.map((entry) => {
            if (entry.id === activeEntry?.id) {
                const predictions = entry.predictions?.filter((pre) => pre.leg_id !== activeLeg?.id) || [];
                predictions.push(
                    ...(submittedPredictions || []).map((pre) => {
                        return {
                            ...pre,
                            leg_id: activeLeg?.id,
                            status: PredictionStatus.PENDING,
                        } as Prediction;
                    }),
                );
                entry.predictions = predictions;
            }
            return entry;
        });

        setEntries(updatedEntries);
    }

    async function sendPrediction() {
        try {
            setIsDisabled(true);
            const body = {
                leg_id: contest!.meta!.active_leg?.id,
                user_id: String(user?.id),
                platform: PredictionPlatform.WEB,
                predictions: getPredictionsForSend(),
            };
            await sendContestPrediction({ body, isResubmit });
            updateEntriesAfterSubmit(body.predictions);
            setInitialSelections((prevState) => {
                if (activeEntry) {
                    prevState[activeEntry.id] = selections[activeEntry.id];
                }

                return prevState;
            });
        } catch (error) {
            logger.error('SportContestsBetslip', 'sendPrediction', error);
        } finally {
            setIsDisabled(false);
        }
    }

    return (
        <Wrapper>
            <SportContestsBetslipHeader name={contest.name} clearSelection={clearSelections} />
            {isLoading ? (
                <UiDotsLoader />
            ) : (
                <main>
                    <SportContestsBetslipCounter
                        selectedOutcomesCount={selectedOutcomesCount}
                        requiredSelections={requiredSelections}
                    />
                    <div className="selections">
                        {getSelections().map((selection) => (
                            <SportContestsBetslipSelection
                                selection={selection}
                                key={selection.outcomeId}
                                onRemove={() => {
                                    removeSelection(selection.marketId);
                                }}
                                activeLeg={activeLeg}
                            />
                        ))}
                    </div>
                </main>
            )}
            <footer>
                {selectedOutcomesCount !== requiredSelections && (
                    <UiAlert failure>
                        {translate(
                            ['required-selections', 'Required amount of selections is {{requiredSelections}}'],
                            'ui.sportsbook.contests',
                            { requiredSelections },
                        )}
                    </UiAlert>
                )}

                <UiButton
                    disabled={selectedOutcomesCount !== requiredSelections || isDisabled || !activeLeg}
                    block
                    size="large"
                    color="primary"
                    onClick={() =>
                        isResubmit
                            ? showModal(SportContestsBetslipResubmitModal, {
                                  params: {
                                      onConfirm: () => {
                                          sendPrediction();
                                          hideModal();
                                      },
                                  },
                                  onClose: hideModal,
                              })
                            : sendPrediction()
                    }
                >
                    {translate(`${isResubmit ? 'Resubmit' : 'Submit'} selection`, 'ui.contests')}
                </UiButton>
            </footer>
        </Wrapper>
    );
}
